/**
 * usePermissions / useCan — UI-гейтинг write-контролов по эффективным правам
 * пользователя (AK-363).
 *
 * Источник — матрица `permissions` из `/api/v1/auth/me` (см. backend
 * `services/access.build_ui_permission_matrix`). Это зеркало реального
 * бэкенд-ACL (`auth.rbac.check_permission`), поэтому UI-решение «показать /
 * скрыть write» всегда совпадает с исходом запроса на сервере — не «толкай и
 * жди 403».
 *
 * Форма матрицы: `{ [resource]: { c, r, u, d } }`. Фронт трактует отсутствие
 * ресурса ИЛИ false одинаково — **действие запрещено**. Пока `/me` не
 * загружен (или упал) — тоже запрещено: **fail-closed**, write-контролы
 * скрыты/дизейблены по умолчанию, а не мигают доступными до ответа.
 *
 * Кэш — react-query по ключу `['auth','me']`, дедуп с любым другим потребителем
 * `/me` в приложении. staleTime 5 мин: права меняются редко (смена роли), но не
 * «навсегда» — переназначение роли подтянется при следующем инвалидейте/фокусе.
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/** Действие над ресурсом: create / read / update / delete. */
export type PermAction = 'c' | 'r' | 'u' | 'd';

/** Права на один ресурс. Любой отсутствующий ключ трактуется как false. */
export type ResourcePerms = Partial<Record<PermAction, boolean>>;

/** Ресурс или «любой из» ресурсов (переходный гейт бэка `A ИЛИ B`). */
export type PermResource = string | readonly string[];

/** Матрица `{resource: {c,r,u,d}}` из `/me`. */
export type PermissionMatrix = Record<string, ResourcePerms>;

/** Минимальная форма `/me`, нужная для прав (остальные поля игнорируем). */
interface MePermissions {
  permissions?: PermissionMatrix;
}

/** react-query ключ для `/me` — общий с остальными потребителями. */
export const ME_QUERY_KEY = ['auth', 'me'] as const;

/**
 * usePermissions — тянет матрицу прав из `/me` (кэш react-query).
 *
 * Возвращает `{ matrix, isLoading, can }`. `can(resource, action)` —
 * fail-closed предикат: неизвестный ресурс / нет данных / загрузка → false.
 */
export function usePermissions() {
  const query = useQuery({
    queryKey: ME_QUERY_KEY,
    queryFn: () => api.get<MePermissions>('/api/v1/auth/me'),
    staleTime: 5 * 60_000,
    // /me критичен и дёшев — не ретраим агрессивно; при 401 api.ts редиректит.
    retry: 1,
  });

  const matrix: PermissionMatrix = query.data?.permissions ?? {};

  /**
   * Список ресурсов = «любой из» (переходные гейты бэка вида `A ИЛИ B`,
   * ADR-081 D5/D6). Пустой список — запрещено.
   */
  const can = (resource: PermResource, action: PermAction): boolean => {
    // Fail-closed: пока грузится или упало — запрещаем (write скрыт).
    if (query.isLoading || query.isError) return false;
    const resources: readonly string[] = typeof resource === 'string' ? [resource] : resource;
    return resources.some((r) => matrix[r]?.[action] === true);
  };

  return { matrix, can, isLoading: query.isLoading, isError: query.isError };
}

/**
 * useCan — сахар для одиночной проверки `can(resource, action)`.
 *
 * ⚠️ Fail-closed: возвращает false пока `/me` не загружен или упал — вызывающий
 * код НЕ должен показывать write-контрол при false.
 *
 * Пример:
 *   const canEditField = useCan('structure', 'u');
 *   const canManageCrops = useCan('sowings', 'c');
 */
export function useCan(resource: PermResource, action: PermAction): boolean {
  const { can } = usePermissions();
  return can(resource, action);
}

/**
 * Ресурсы, на которые UI полей/культур смотрит при гейтинге write-контролов.
 * Держим здесь как единый словарь, чтобы не рассыпать строковые литералы по
 * компонентам и не разойтись с бэкендом.
 *
 * ⚠️ Структурные writes полей (создать/править/архивировать поле, add-field,
 * контуры, отделение) бэк гейтит фабрикой `require_farm_structure_write`
 * (ADR-081 D5): `structure` (tenant-writer'ы) ИЛИ `farm_structure` (админ
 * хозяйства, в своём хозяйстве), НЕ `fields`. Поэтому кнопки полей смотрят на
 * `PERM.fieldWrite`, а не на `fields`. Перенос поля в другое хозяйство —
 * только `structure` (на пути хозяйства бэк отвечает 403).
 */
export const PERM = {
  /** Создать/править/архивировать/удалить поле, контуры, отделение (ADR-081 D5). */
  fieldWrite: ['structure', 'farm_structure'] as const,
  /**
   * Передать поле в другое хозяйство (`POST /fields/{id}/transfers`). Бэк —
   * `require_structure_write`, только tenant-level `structure`: на пути
   * хозяйства перенос между хозяйствами = 403 (ADR-081 D5). НЕ `fieldWrite` —
   * иначе админ хозяйства видит кнопку и упирается в 403.
   */
  fieldTransfer: 'structure' as const,
  /**
   * Переместить поля в отделение (`POST /farms/{id}/fields/move-to-brigade`).
   * Бэк пока `require_structure_write` (tenant-level). Открытие ручки админу
   * хозяйства по D5 — AK-930 (head-backend); после него — `fieldWrite`.
   */
  fieldMoveToBrigade: 'structure' as const,
  /** Посевы / культуры (create/update/delete sowing). */
  cropWrite: 'sowings' as const,
  /**
   * Правка СОДЕРЖИМОГО поля: имя, граница, подзоны, рисовая система
   * (`PATCH /fields/{id}`, геометрия, подзоны — AK-929). Бэк гейтит их
   * `require_farm_structure_write(·, resource="fields")`: tenant-writer
   * (`structure`) ИЛИ роль хозяйства с `fields:U` в своём хозяйстве — так
   * гл. агроном хозяйства ведёт поле, но не заводит и не архивирует (D9).
   *
   * Ключ `field_content` — не ресурс каталога, а проекция этого гейта в `/me`
   * (`auth.rbac.build_farm_gate_ui_rows`). Ни `fields` (там `u` есть и у
   * легаси `agronom`, которого ручка не пускает → кнопка и 403), ни
   * `fieldWrite` (у гл. агронома ложь → кнопка спрятана, хотя ручка пускает)
   * сюда не годятся. Заведение / архив / восстановление / угодья / жизненный
   * цикл — по-прежнему `fieldWrite`.
   */
  fieldContentWrite: 'field_content' as const,
  /**
   * Тех. операции и работы внутри них (AK-525). Бэк гейтит и `operations`, и
   * `works` на один ресурс `operations` (см. `api/v1/works.py`) — поэтому
   * кнопки работ смотрят сюда же.
   */
  operationWrite: 'operations' as const,
  /**
   * Осмотры полей (founder 16.08.2026 — создание осмотра с веба). Бэк гейтит
   * `POST /inspections` и presign фото на ресурс `scoutings` (см.
   * `api/v1/inspections.py`).
   */
  scoutingWrite: 'scoutings' as const,
  /**
   * Парк техники хозяйства (AK-747). Бэк (ADR-081 D6, AK-883) гейтит
   * `/company/machinery*` переходным `company_reference ИЛИ machinery`: у админа
   * хозяйства есть `machinery`, но нет `company_reference`; у кастомных ролей
   * со справочниками — наоборот. Снятие `ИЛИ` — AK-887, тогда и здесь (AK-916).
   */
  machineryWrite: ['company_reference', 'machinery'] as const,
} satisfies Record<string, PermResource>;
